import React, { useEffect, useState, useRef } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { wordpressApi, WordPressEvent } from '../services/wordpress';
import { useLocation, useNavigate } from 'react-router-dom';
import './Events.css';
import EventRow from './Events/EventRow';
import PageWrapper from './common/PageWrapper';
import SectionHeader from './common/SectionHeader';
import {
  applyDocumentBackground,
  getMonthGradient,
  getMonthGraffitiColor,
  resolveCssColor,
} from '../utils/backgroundUtils';
import { useLocalization } from '../localization';

interface EventsProps {
  order?: 'asc' | 'desc';
  past?: boolean;
  title?: string;
}

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const PAST_EVENTS_BATCH_SIZE = 24;

function getHueFromRGB(color: string): number {
  // Remove whitespace and '#' if present
  color = color.trim().replace('#', '');

  // Convert hex to RGB values
  const r = parseInt(color.slice(0, 2), 16) / 255;
  const g = parseInt(color.slice(2, 4), 16) / 255;
  const b = parseInt(color.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;

  if (max === min) {
    h = 0; // achromatic
  } else {
    const d = max - min;
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  return h;
}

function hexToRgb(hex: string) {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3)
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  const num = parseInt(hex, 16);
  return [num >> 16, (num >> 8) & 255, num & 255];
}

function rgbToHex([r, g, b]: Array<number>) {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function interpolateColor(c1: string, c2: string, t: number) {
  const rgb1 = hexToRgb(c1);
  const rgb2 = hexToRgb(c2);
  const interpolated = rgb1.map((v, i) => Math.round(v + (rgb2[i] - v) * t));
  return rgbToHex(interpolated);
}

const blendGradients = (
  grad1_col1: string,
  grad1_col2: string,
  grad2_col1: string,
  grad2_col2: string,
  t: number
): string => {
  const interpol1 = interpolateColor(
    resolveCssColor(grad1_col1),
    resolveCssColor(grad2_col1),
    t
  );
  const interpol2 = interpolateColor(
    resolveCssColor(grad1_col2),
    resolveCssColor(grad2_col2),
    t
  );

  return `linear-gradient(to bottom, ${interpol1}, ${interpol2})`;
};

const applyGraffitiColor = (color: string) => {
  document.documentElement.style.setProperty('--active-graffiti-color', color);
};

const applyBackground = (background: string) => {
  applyDocumentBackground(background);
};

const isMobileViewport = () => window.matchMedia(MOBILE_MEDIA_QUERY).matches;

const getBackgroundFromMonth = (month?: number | string): string => {
  return getMonthGradient(month);
};

const getGraffitiColorFromMonth = (month?: number | string): string => {
  return getMonthGraffitiColor(month);
};

const applyStaticMonthBackground = (month?: number | string) => {
  applyBackground(getBackgroundFromMonth(month));
  applyGraffitiColor(getGraffitiColorFromMonth(month));
};

const computeBackgroundGradient = () => {
  const eventRows = document.querySelectorAll('.event-row');
  
  // If no events, use default month 1
  if (!eventRows.length) {
    const defaultGradient = getMonthGradient();
    applyBackground(defaultGradient);
    applyGraffitiColor(getMonthGraffitiColor());
    return;
  }

  // Find first visible row and next row
  let currentRow: HTMLElement | null = null;
  let nextRow: HTMLElement | null = null;
  let progress = 0;

  // Convert NodeList to Array for easier manipulation
  const rows = Array.from(eventRows) as HTMLElement[];
  
  // Find first visible row (crossing viewport top)
  for (let i = 0; i < rows.length; i++) {
    const rect = rows[i].getBoundingClientRect();
    
    if (rect.bottom > 0) {
      currentRow = rows[i];
      nextRow = rows[i + 1] || null;
      
      // Calculate progress between current and next row
      if (nextRow) {
        const currentRect = currentRow.getBoundingClientRect();
        const nextRect = nextRow.getBoundingClientRect();
        const total = nextRect.top - currentRect.top;
        const current = -currentRect.top; // How far we've scrolled past the current row
        progress = Math.max(0, Math.min(1, current / total));
      }
      break; // Stop searching once we find the first visible row
    }
  }

  // If no visible rows found, use first row
  if (!currentRow) {
    currentRow = rows[0];
    nextRow = rows[1] || null;
    progress = 0;
  }

  // Get months from data attributes
  const currentMonth = parseInt(currentRow.dataset.month || '1', 10);
  const nextMonth = nextRow ? parseInt(nextRow.dataset.month || '1', 10) : currentMonth;
  const currentAccentMonth = currentMonth >= 1 && currentMonth < 12 ? currentMonth + 1 : currentMonth;
  const nextAccentMonth = nextMonth >= 1 && nextMonth < 12 ? nextMonth + 1 : nextMonth;

  // Apply gradient
  const blended = blendGradients(
    `var(--${currentMonth}-secondary)`,
    `var(--${currentAccentMonth}-secondary)`,
    `var(--${nextMonth}-secondary)`,
    `var(--${nextAccentMonth}-secondary)`,
    progress
  );
  const blendedGraffitiColor = interpolateColor(
    resolveCssColor(`var(--${currentMonth}-principal)`),
    resolveCssColor(`var(--${nextMonth}-principal)`),
    progress
  );

  applyBackground(blended);
  applyGraffitiColor(blendedGraffitiColor);
};

const Events: React.FC<EventsProps> = ({
  order = 'asc',
  past = false,
  title,
}) => {
  const { copy, locale } = useLocalization();
  const [events, setEvents] = useState<WordPressEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);
  const [visiblePastEventsCount, setVisiblePastEventsCount] = useState(
    PAST_EVENTS_BATCH_SIZE
  );
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleEvents = past
    ? events.slice(0, visiblePastEventsCount)
    : events;
  const hasMorePastEvents = past && visibleEvents.length < events.length;

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wordpressApi.getEvents('event_date', order, past);
        setEvents(response.data);

        // Check if there's an event ID in the URL
        const params = new URLSearchParams(location.search);
        const eventId = params.get('event');
        if (eventId && !isMobileViewport()) {
          const id = parseInt(eventId.split('_')[1], 10);
          setExpandedEventId(id);

          // Scroll the event into view
          setTimeout(() => {
            const element = document.getElementById(`event-${id}`);
            if (!element) return;
            
            const headerOffset = 100;
            const elementHeight = element.offsetHeight;
            const viewportHeight = window.innerHeight;
            
            // Get element's position relative to the viewport
            const elementPosition = element.getBoundingClientRect().top;
            
            // If element is smaller than viewport, center it
            if (elementHeight < viewportHeight) {
              const centerOffset = (viewportHeight - elementHeight) / 2;
              const offsetPosition = elementPosition + window.pageYOffset - centerOffset;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            } else {
              // If element is larger, align to top with header offset
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          });
        } else if (isMobileViewport()) {
          setExpandedEventId(null);
        }
      } catch (err) {
        setError(copy.events.loadError);
        console.error('Error fetching events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [copy.events.loadError, location.search, order, past]);

  useEffect(() => {
    setVisiblePastEventsCount(PAST_EVENTS_BATCH_SIZE);
  }, [past, order]);

  useEffect(() => {
    if (!past) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (!params.has('page')) {
      return;
    }

    params.delete('page');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, past]);

  useEffect(() => {
    if (isMobileViewport()) {
      const activeEvent = expandedEventId
        ? visibleEvents.find((event) => event.id === expandedEventId)
        : visibleEvents[0];
      const activeMonth = activeEvent?.acf?.event_date?.split('/')[1];

      applyStaticMonthBackground(activeMonth);
      return;
    }

    const observer = new MutationObserver(() => {
      const eventRows = document.querySelectorAll('.event-row');
      if (eventRows.length > 0) {
        computeBackgroundGradient(); // Compute background gradient once rows are available
        observer.disconnect(); // Stop observing once the rows are populated
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const handleScroll = () => {
      computeBackgroundGradient();
    };

    document?.addEventListener('scroll', handleScroll);

    return () => {
      document?.removeEventListener('scroll', handleScroll);
      observer.disconnect(); // Clean up the observer
    };
  }, [visibleEvents, expandedEventId]);

  const toggleExpand = (eventId: number) => {
    if (isMobileViewport()) {
      return;
    }

    const newExpandedId = expandedEventId === eventId ? null : eventId;
    setExpandedEventId(newExpandedId);

    if (isMobileViewport()) {
      const activeEvent = newExpandedId
        ? events.find((event) => event.id === newExpandedId)
        : events[0];
      const activeMonth = activeEvent?.acf?.event_date?.split('/')[1];

      applyStaticMonthBackground(activeMonth);
    }

    setTimeout(() => {
      const element = document.getElementById(`event-${eventId}`);
      if (!element) return;
      
      const headerOffset = 100;
      const elementHeight = element.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // Get element's position relative to the viewport
      const elementPosition = element.getBoundingClientRect().top;
      
      // If element is smaller than viewport, center it
      if (elementHeight < viewportHeight) {
        const centerOffset = (viewportHeight - elementHeight) / 2;
        const offsetPosition = elementPosition + window.pageYOffset - centerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      } else {
        // If element is larger, align to top with header offset
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    });

  };

  const handleLoadMore = () => {
    setVisiblePastEventsCount((currentCount) => currentCount + PAST_EVENTS_BATCH_SIZE);
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setExpandedEventId(null); // Collapse the expanded event
      if (isMobileViewport()) {
        applyStaticMonthBackground(visibleEvents[0]?.acf?.event_date?.split('/')[1]);
      }
      const params = new URLSearchParams(location.search);
      params.delete('event');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{copy.events.loading}</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <div onClick={handleBackgroundClick} style={{ width: '100%' }}>
      <Container ref={containerRef}>
        <PageWrapper
          title={title ?? copy.events.title}
          containerClassName="events-page-container page-container-complete-border"
          afterContainer={
            hasMorePastEvents ? (
              <div className="events-pagination events-pagination-outside">
                <button
                  type="button"
                  className="events-pagination-button"
                  onClick={handleLoadMore}
                >
                  {copy.events.loadMore}
                </button>
              </div>
            ) : null
          }
        >
          {visibleEvents.map((event, index) => {
            const isExpanded = expandedEventId === event.id;
            const currentMonth = event.acf?.event_date?.split('/')[1];
            const previousMonth =
              index > 0
                ? visibleEvents[index - 1].acf?.event_date?.split('/')[1]
                : null;
            const isNewMonth =
              !previousMonth || currentMonth !== previousMonth;
            const primColor = getComputedStyle(
              document.documentElement
            ).getPropertyValue(`--${parseInt(currentMonth!)}-principal`);
            const primColorHue = getHueFromRGB(primColor);

            return (
              <React.Fragment key={event.id}>
                {isNewMonth && (
                  <SectionHeader 
                    title={new Date(2000, parseInt(currentMonth!) - 1).toLocaleDateString(locale, {
                      month: 'long',
                    })}
                  />
                )}
                <EventRow
                  event={event}
                  isExpanded={isExpanded}
                  toggleExpand={toggleExpand}
                  currentMonth={currentMonth!}
                  primColorHue={primColorHue}
                  showTicketLink={!past}
                />
              </React.Fragment>
            );
          })}
        </PageWrapper>
      </Container>
    </div>
  );
};

export default Events;
